import React, { useState, useEffect } from 'react';
import { useRouter } from 'next/router';
import { useDispatch } from 'react-redux';
import { useDeepCompareEffect } from 'react-use';
import { useTableState } from '@dotone/react-ui-core/hooks';
import { useCurrentUser, useShallowEqualSelector, useTranslate } from '@hooks';
import { downloadStatSummaries, fetchStatSummaries } from '@actions';
import { statSummaryDefaultColumns } from '@constants';
import { snakeCase, camelCase, compact } from 'lodash';
import { PeriodTabNav, DownloadDropdown } from '@dotone/react-ui-core';
import StatSummaryTable from './StatSummaryTable';

const StatSummary = ({ reportType, dateType }) => {
  const { t } = useTranslate('statSummary', `pages.${reportType}Performance.index`);
  const dispatch = useDispatch();
  const router = useRouter();
  const { currentUser, currency, timeZone, locale } = useCurrentUser();

  const isPerformance = dateType === 'recordedAt';
  const defaultColumns = compact([
    ...statSummaryDefaultColumns[isPerformance ? 'performance' : reportType],
    isPerformance ? camelCase(`${reportType}_id`) : 'offerId',
  ]);
  const [downloading, setDownloading] = useState(false);
  const [period, setPeriod] = useState('all');
  const [tableProps, setTableProps] = useTableState({
    dateable: true,
    defaultColumns,
    columnType: camelCase(`${reportType}Report`),
  });
  const { filters, columns, sortField, sortOrder, page, perPage } = tableProps;
  const periodical = period && period !== 'all';
  const queryColumns = [
    ...(columns.length ? columns : defaultColumns),
    periodical ? 'date' : null,
  ].filter((col) => col);

  const query = {
    locale,
    currency,
    timeZone,
    ...filters,
    dimension: isPerformance ? snakeCase(`${reportType}_id`) : filters.dimension || 'offerId',
    period: period === 'all' ? null : period,
    sortField,
    sortOrder,
    page,
    perPage,
    columns: queryColumns,
    ...(periodical && { sortField: sortField || 'date', sortOrder: sortOrder || 'desc' }),
    dateType: snakeCase(dateType),
  };

  const { downloadStatus } = useShallowEqualSelector(({ statSummaryDownload }) => ({
    downloadStatus: statSummaryDownload.status,
  }));

  const { data, total, isLoading, pagination } = useShallowEqualSelector(
    ({ statSummaryList }) => statSummaryList
  );

  const handleDownload = (downloadFormat) => {
    setDownloading(true);
    dispatch(downloadStatSummaries({ ...query, downloadTitle: t('title'), downloadFormat }));
  };

  useDeepCompareEffect(() => {
    if (currentUser.id) {
      dispatch(fetchStatSummaries(query, reportType));
    }
  }, [dispatch, currentUser.id, query, reportType]);

  useEffect(() => {
    if (downloading && downloadStatus === 'success') {
      router.push('/advertisers/downloads');
    }
  }, [router, downloading, downloadStatus]);

  return (
    <StatSummaryTable
      reportType={isPerformance ? 'performance' : reportType}
      periodical={period !== 'all'}
      header={<PeriodTabNav activeTab={period} onClick={setPeriod} />}
      tableProps={tableProps}
      loading={isLoading}
      data={isLoading ? [] : data}
      dataTotal={data.length ? total : {}}
      pagination={pagination}
      onTableChange={setTableProps}
      actions={
        isPerformance ? (
          <DownloadDropdown
            disabled={!data.length || isLoading}
            totalCount={pagination.totalEntries}
            buttonProps={{ submitting: downloading }}
            onClick={handleDownload}
          />
        ) : null
      }
    />
  );
};

export default StatSummary;
